import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Card, Divider, Select, Switch, DatePicker, Form, Table, Button, Dropdown, Modal } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import moment from "moment";
import { getCustomerStatement } from "../../redux/dashboard/dashboardActions";
import { formatCurrency, formatDate } from "../../utils/utils";
import { Resizable } from "react-resizable";
import ReactDragListView from "react-drag-listview";

const ResizableTitle = (props) => {
    const { onResize, width, ...restProps } = props;

    if (!width) {
        return <th {...restProps} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={
                <span
                    className="react-resizable-handle"
                    onClick={(e) => e.stopPropagation()}
                />
            }
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th {...restProps} />
        </Resizable>
    );
};

const getDateRange = (option) => {
    const today = new Date();
    let startDate, endDate;

    const startOfDay = (date) => moment(date).startOf('day');
    const endOfDay = (date) => moment(date).endOf('day');

    const startOfWeek = (date) => {
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return startOfDay(new Date(date.setDate(diff)));
    };

    const startOfMonth = (date) => startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
    const startOfYear = (date) => startOfDay(new Date(date.getFullYear(), 0, 1));

    switch (option) {
        case 'today':
            startDate = startOfDay(new Date());
            endDate = endOfDay(new Date());
            break;
        case 'thisWeek':
            startDate = startOfWeek(new Date());
            endDate = endOfDay(new Date());
            break;
        case 'thisMonth':
            startDate = startOfMonth(new Date());
            endDate = endOfDay(new Date());
            break;
        case 'last6Months':
            startDate = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 5, 1));
            endDate = endOfDay(new Date());
            break;
        case 'thisYear':
            startDate = startOfYear(new Date());
            endDate = endOfDay(new Date());
            break;
        default:
            startDate = startOfDay(new Date());
            endDate = endOfDay(new Date());
            break;
    }

    return [startDate, endDate];
};

export default function StatementTable({ isVisible, onCancel }) {
    const formRef = useRef(null);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [statementData, setStatementData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [switchDate, setSwitchDate] = useState(false);
    const [visible, setVisible] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState(getDateRange('last6Months'));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [fromDate, toDate] = selectedDateRange?.map(date => date.format('YYYY-MM-DD'));

                const customerStatementResult = await getCustomerStatement({
                    cardCode: localStorage.getItem('cardCode'),
                    fromDate,
                    toDate,
                });

                if (customerStatementResult && customerStatementResult?.length > 0) {
                    const parsedData = JSON.parse(customerStatementResult);
                    setStatementData(parsedData);


                    const openingBalance = parsedData?.[0]?.Opening || 0;
                    setOpeningBalance(openingBalance);

                    const docNoFilters = [...new Set(parsedData?.map(item => item.baseRef))].map(val => ({ text: val, value: val }));
                    const refNoFilters = [...new Set(parsedData?.map(item => item.refNo))].map(val => ({ text: val, value: val }));
                    const particularsFilters = [...new Set(parsedData?.map(item => item.particulars))].filter(val => val !== null && val !== undefined).map(val => ({ text: val, value: val }));
                    const refDateFilters = [...new Set(parsedData?.map(item => item.refDate))].map(val => ({ text: formatDate(val), value: val }));
                    const taxDateFilters = [...new Set(parsedData?.map(item => item.taxDate))].map(val => ({ text: formatDate(val), value: val }));
                    const debitFilters = [...new Set(parsedData?.map(item => item.debit))].map(val => ({ text: formatCurrency(val), value: val }));
                    const creditFilters = [...new Set(parsedData?.map(item => item.credit))].map(val => ({ text: formatCurrency(val), value: val }));

                    setColumns([
                        {
                            title: 'Doc No',
                            dataIndex: 'baseRef',
                            width: 80,
                            sorter: (a, b) => a.baseRef - b.baseRef,
                            filterSearch: true,
                            filters: docNoFilters,
                            onFilter: (value, record) => record?.baseRef === value,
                        },
                        {
                            title: 'Ref No',
                            dataIndex: 'refNo',
                            width: 100,
                            sorter: (a, b) => a.refNo - b.refNo,
                            filterSearch: true,
                            filters: refNoFilters,
                            onFilter: (value, record) => record?.refNo === value,
                        },
                        {
                            title: 'Particulars',
                            dataIndex: 'particulars',
                            key: 'particulars',
                            width: 200,
                            sorter: (a, b) => (a.particulars || '').localeCompare(b.particulars || ''),
                            filterSearch: true,
                            filters: particularsFilters,
                            onFilter: (value, record) => (record?.particulars || '') === value,
                        },
                        {
                            title: 'Ref Date',
                            dataIndex: 'refDate',
                            render: (text, record) => <div className={'hp-text-center'}>{formatDate(record?.refDate)}</div>,
                            sorter: (a, b) => new Date(a.refDate) - new Date(b.refDate),
                            width: 120,
                            filterSearch: true,
                            filters: refDateFilters,
                            onFilter: (value, record) => record?.refDate === value,
                        },
                        {
                            title: 'Doc Date',
                            dataIndex: 'taxDate',
                            render: (text, record) => <div className={'hp-text-center'}>{formatDate(record?.taxDate)}</div>,
                            sorter: (a, b) => new Date(a.taxDate) - new Date(b.taxDate),
                            width: 120,
                            filterSearch: true,
                            filters: taxDateFilters,
                            onFilter: (value, record) => record?.taxDate === value,
                        },
                        {
                            title: 'Debit',
                            dataIndex: 'debit',
                            key: 'debit',
                            width: 150,
                            render: (text, record) =>
                                record?.type === 'D' ? (
                                    <div className="hp-text-right">{formatCurrency(record?.debit)}</div>
                                ) : (
                                    <div className="hp-text-center">-</div>
                                ),
                            sorter: (a, b) => a.debit - b.debit,
                            filterSearch: true,
                            filters: debitFilters,
                            onFilter: (value, record) => record?.debit === value,
                        },
                        {
                            title: 'Credit',
                            dataIndex: 'credit',
                            key: 'credit',
                            width: 150,
                            render: (text, record) =>
                                record?.type === 'C' ? (
                                    <div className="hp-text-right">{formatCurrency(record?.credit)}</div>
                                ) : (
                                    <div className="hp-text-center">-</div>
                                ),
                            sorter: (a, b) => a.credit - b.credit,
                            filterSearch: true,
                            filters: creditFilters,
                            onFilter: (value, record) => record?.credit === value,
                        },
                    ]);
                }
            } catch (error) {
            }
        };

        fetchData();
    }, [selectedDateRange]);


    useEffect(() => {
        if (switchDate) {
            setSelectedDateRange(getDateRange('last6Months'));
            formRef?.current?.setFieldsValue({ datePicker: getDateRange('last6Months') });
        } else {
            setSelectedDateRange(getDateRange('last6Months'));
            formRef?.current?.setFieldsValue({ dateRange: 'last6Months' });
        }
    }, [switchDate]);

    const totalDebit = statementData?.reduce((total, item) => total + item.debit, 0);
    const totalCredit = statementData?.reduce((total, item) => total + item.credit, 0);
    const closingBalance = openingBalance + (totalDebit - totalCredit);

    const handleSwitchDate = (checked) => {
        setSwitchDate(checked);
    };

    const handleDateRangeChange = (value) => {
        const range = getDateRange(value);
        setSelectedDateRange(range);
        setVisible(false);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
    };

    const handleDropdownVisibleChange = (flag) => {
        setVisible(flag);
    };

    const handleDatePickerChange = (dates) => {
        if (dates && dates.length === 2) {
            const [startDate, endDate] = dates;
            if (moment.isMoment(startDate) && moment.isMoment(endDate)) {
                setSelectedDateRange([startDate, endDate]);
                setVisible(false);
            }
        }
    };

    const handleResize = (index) => (e, { size }) => {
        setColumns((prevColumns) => {
            const nextColumns = [...prevColumns];
            nextColumns[index] = { ...nextColumns[index], width: size.width };
            return nextColumns;
        });
    };

    const dragProps = {
        onDragEnd: (fromIndex, toIndex) => {
            const updatedColumns = [...columns];
            const [movedColumn] = updatedColumns.splice(fromIndex, 1);
            updatedColumns.splice(toIndex, 0, movedColumn);
            setColumns(updatedColumns);
        },
        nodeSelector: "th",
        handleSelector: ".dragHandler",
        ignoreSelector: "react-resizable-handle"
    };

    const mergedColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
            width: column.width,
            onResize: handleResize(index)
        })
    }));

    const menu = (
        <div onClick={handleMenuClick}>
            <Card style={{ width: '350px' }} >
                <Form layout="vertical" ref={formRef}>
                    <Form.Item name={'date'} className="hp-mb-12">
                        <span className="hp-font-weight-600">Custom Date :</span><Switch checked={switchDate} onChange={handleSwitchDate} className="hp-ml-12" />
                    </Form.Item>
                    {switchDate ? (
                        <Form.Item label="Date Range :" className="hp-mb-0" name={'datePicker'}>
                            <DatePicker.RangePicker
                                allowClear={false}
                                className="hp-w-100"
                                value={selectedDateRange}
                                onChange={handleDatePickerChange}

                                format="MM/DD/YYYY"
                            />
                        </Form.Item>
                    ) : (
                        <Form.Item label="Date Range :" className="hp-mb-0" name={'dateRange'}>
                            <Select defaultValue="last6Months" className="hp-w-100" onChange={handleDateRangeChange}>
                                <Option value="today">Today</Option>
                                <Option value="thisWeek">This Week</Option>
                                <Option value="thisMonth">This Month</Option>
                                <Option value="last6Months">Last 6 Months</Option>
                                <Option value="thisYear">This Year</Option>
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Card>
        </div>
    );

    return (
        <>
            <Modal
                title="Statement Report"
                visible={isVisible}
                onCancel={onCancel}
                footer={null}
                width="70%"
            >
                <Row gutter={[18, 18]}>
                    <Col span={24}>
                        <Row gutter={[18, 18]} justify="space-between" align="middle">
                            <Col>
                                <Card bordered={false} className="balance-card">
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontWeight: 500, fontSize: 14, color: "#1b3c87" }}>
                                                Opening Balance:
                                            </span>
                                            <span style={{ fontWeight: 500, fontSize: 14, color: "#222" }}>
                                                {formatCurrency(openingBalance)}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: 13, color: "#666" }}>
                                            <span>
                                                <strong>From:</strong> {selectedDateRange[0]?.format("MM-DD-YYYY")}
                                            </span>
                                            <span>
                                                <strong>To:</strong> {selectedDateRange[1]?.format("MM-DD-YYYY")}
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col>
                                <Dropdown
                                    overlay={menu}
                                    trigger={['click']}
                                    placement="bottomRight"
                                    getPopupContainer={triggerNode => triggerNode.parentNode}
                                    visible={visible}
                                    onVisibleChange={handleDropdownVisibleChange}
                                >
                                    <Button type="primary" ghost>
                                        Filter <FilterOutlined className="hp-ml-8" />
                                    </Button>
                                </Dropdown>
                            </Col>
                        </Row>
                    </Col>

                    <Col span={24}>
                        <ReactDragListView.DragColumn {...dragProps}>
                            <Table
                                size="small"
                                columns={mergedColumns}
                                components={{ header: { cell: ResizableTitle } }}
                                dataSource={statementData}
                                pagination={true}
                                bordered
                                summary={() => (
                                    <>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell colSpan={5}>
                                                <div className="hp-text-right hp-font-weight-600">Total</div>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell>
                                                <div className="hp-text-right hp-font-weight-600">{formatCurrency(totalDebit)}</div>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell>
                                                <div className="hp-text-right hp-font-weight-600">{formatCurrency(totalCredit)}</div>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell colSpan={5}>
                                                <div className="hp-text-right hp-font-weight-600">Closing Balance</div>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell colSpan={2}>
                                                <div className="hp-text-right hp-font-weight-600">{formatCurrency(closingBalance)}</div>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </>
                                )}
                            />
                        </ReactDragListView.DragColumn>
                    </Col>
                </Row>
            </Modal>
        </>
    );
};
